import { LS, Cookie } from './storage';
import { SYNC_URL, SyncQueue } from './sync';
import { SentryUtil } from './sentry';
import { applyBrand } from './brand';

// ─── #1 Auth Token (signed sync requests) ───
export const AuthToken={
  // Legacy — kept for backward compat during migration
  init:(_email)=>{},
  generate:(_email)=>"",
  getHeaders:(email)=>{
    const sessionToken=LS.get("ft-session-token");
    return{
      "Content-Type":"application/json",
      ...(sessionToken?{"X-Session-Token":sessionToken}:{}),
      "X-Auth-Token":email||"legacy",
    };
  },
};

// Session management
export const SessionManager={
  // Create session after signup (called with email + PIN)
  create:async(email,pin,deviceId)=>{
    try{
      const ctrl=new AbortController();
      const timer=setTimeout(()=>ctrl.abort(),10000);
      const res=await fetch(`${SYNC_URL}/api/auth/session`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({action:"create",email:email.toLowerCase(),pin,deviceId:deviceId||LS.get("ft-device-id")}),
        signal:ctrl.signal
      });
      clearTimeout(timer);
      const json=await res.json();
      if(json.success&&json.token){
        LS.set("ft-session-token",json.token);
        LS.set("ft-session-email",email.toLowerCase());
        LS.set("ft-session-expires",json.expires_at);
        Cookie.set("ironlog_session",json.token,30); // SSO across subdomains
        return{success:true,token:json.token};
      }
      return{success:false,error:json.error,attempts_remaining:json.attempts_remaining};
    }catch(e){return{success:false,error:"Connection failed"};}
  },
  // Check if we have a valid session
  hasSession:()=>{
    const token=LS.get("ft-session-token");
    const expires=LS.get("ft-session-expires");
    if(!token)return false;
    if(expires){
      const t=Date.parse(expires);
      // Treat unparseable expiry as expired — fail closed
      if(!Number.isFinite(t)||t<Date.now()){
        LS.set("ft-session-token",null);
        LS.set("ft-session-expires",null);
        return false;
      }
    }
    return true;
  },
  // Revoke (logout)
  revoke:async({clearQueue=true}={})=>{
    const token=LS.get("ft-session-token");
    SentryUtil.breadcrumb("User signed out","auth");SentryUtil.reset();
    // Drop anything this account queued but never managed to upload. The queue used to
    // outlive sign-out, so the next person to sign in on this browser replayed it under
    // THEIR session — and because the row ids are global primary keys, the server's
    // upsert moved the previous user's workouts, meals, measurements and photos into the
    // new account rather than duplicating them. Unsent work is lost; someone else's
    // history landing in your account is worse.
    if(clearQueue)try{SyncQueue.clear(()=>!LS.get("ft-session-token"));}catch(e){}
    LS.set("ft-session-token",null);LS.set("ft-session-email",null);LS.set("ft-session-expires",null);LS.set("ft-is-admin",null);LS.set("ft-is-super-admin",null);
    // White-label: drop the tenant cache + splash brand and revert to the IronLog look
    LS.del("ft-org");LS.del("ft-org-brand");LS.del("ft-pending-join");
    try{applyBrand(null);}catch(e){}
    Cookie.clear("ironlog_session"); // Clear SSO cookie
    if(token){try{await fetch(`${SYNC_URL}/api/auth/session`,{method:"POST",headers:{"Content-Type":"application/json","X-Session-Token":token},body:JSON.stringify({action:"revoke",token}),signal:AbortSignal.timeout(10000),keepalive:true});}catch(e){}}
  },
  // Check admin status (silent — never exposes admin to non-admins)
  // Requires a real session token; email alone is client-asserted and not trusted.
  //
  // Returns the whole `check` payload — { isAdmin, isSuperAdmin, adminEmailConfigured } —
  // not just the boolean, so the client can gate the super-admin-only tenant screens
  // instead of showing them to every admin and failing later with `super_admin_required`.
  // `adminEmailConfigured: false` means ADMIN_EMAIL is unset on the API: nobody is the
  // super-admin, which is a misconfiguration to name rather than a permissions decision.
  // The only callers are in App.jsx and both read `.isAdmin`; the shape is documented
  // here because an object is always truthy, so a caller that branched on the bare
  // result would silently treat a non-admin as an admin.
  checkAdmin:async()=>{
    const deny=()=>{LS.set("ft-is-admin",null);LS.del("ft-is-super-admin");
      return{isAdmin:false,isSuperAdmin:false,adminEmailConfigured:false};};
    const token=LS.get("ft-session-token");
    if(!token)return deny();
    if(!SessionManager.hasSession())return deny();
    try{
      const headers={"Content-Type":"application/json","X-Session-Token":token};
      const res=await fetch(`${SYNC_URL}/api/admin`,{method:"POST",headers,
        body:JSON.stringify({action:"check"})});
      if(res.ok){
        const json=await res.json();
        if(json.isAdmin){
          // true / false / null. null means this API build did not answer the
          // question (the field predates `check` reporting it) — the caller must
          // not read that as "no", or every admin loses the tenant screens the
          // moment they talk to an older deployment. The API is the real gate.
          const su=Object.prototype.hasOwnProperty.call(json,"isSuperAdmin")?!!json.isSuperAdmin:null;
          const out={isAdmin:true,isSuperAdmin:su,
            adminEmailConfigured:json.adminEmailConfigured!==false};
          LS.set("ft-is-admin",true);
          // Cached next to ft-is-admin and read optimistically on boot. The server
          // re-verifies on every admin navigation, so a flipped flag survives one
          // round trip at most.
          if(su===null)LS.del("ft-is-super-admin");else LS.set("ft-is-super-admin",su);
          return out;
        }
      }
      return deny();
    }catch(e){
      // Network failure: leave the cached flags alone (an offline admin keeps the tab)
      // but report no elevation for this attempt.
      return{isAdmin:false,isSuperAdmin:false,adminEmailConfigured:false};
    }
  },
};
